import React, { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { useDispatch } from 'react-redux';
import * as XLSX from 'xlsx';
import jsPDF from "jspdf";
import "jspdf-autotable";

import './ProjectReportTableStyle.css';
import ProjectReportSearchFilter from '../ProjectReportSearchFilter/ProjectReportSearchFilter';
import Loader from '../../../../Components/Loader/Loader';
import { get_project_reports_with_pagination } from '../../../../config/ReportApis/projectReportApis';
import { EllipsisWithTooltip } from '../../../../Components/custom_components/CustomTooltip/EllipsisWithTooltip';
import { InfoToast } from '../../../../utils/toast';
import { closeExportLoader, openExportLoader } from '../../../../features/slices/exportSlice';

function ProjectReportTable() {
    const dispatch = useDispatch();
    const [projectReport, setProjectReport] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [filterValues, setFilterValues] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [totalItemPerPage, setTotalItemPerPage] = useState(25);
    const [isExportingPDF, setIsExportingPDF] = useState(false);
    const [isExportingExcel, setIsExportingExcel] = useState(false);

    const columns = [
        'parentSite', 'subSiteName',
        'january', 'february', 'march', 'april', 'may', 'june',
        'july', 'august', 'september', 'october', 'november', 'december'
    ];

    useEffect(() => {
        fetchProjectReports();
    }, [pageNumber, totalItemPerPage, filterValues]);

    // Transform API data
    const transformReportData = (data) =>
        data.map(site => ({
            parentSite: site.parentSite,
            sub_sites: site.sub_sites.map(sub => {
                const subSite = { subSiteName: sub?.subSiteName || "—" };
                Object.entries(sub).forEach(([month, value]) => {
                    if (month !== "subSiteName" && typeof value === "object" && value !== null) {
                        subSite[month.toLowerCase()] = value?.technology || [];
                        subSite[`${month.toLowerCase()}_cancel`] = value?.cancel_technology || [];
                    }
                });
                return subSite;
            }),
        }));

    const fetchProjectReports = async () => {
        const formData = new FormData();
        formData.append('page', pageNumber);
        formData.append('pageItemcount', totalItemPerPage);
        Object.keys(filterValues).forEach(key => formData.append(key, filterValues[key]));

        try {
            setIsLoading(true);
            const response = await get_project_reports_with_pagination(formData);
            if (response?.status === "success") {
                setProjectReport(transformReportData(response?.data || []));
            } else {
                setProjectReport([]);
            }
        } catch (error) {
            console.error("Error fetching project reports:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const groupedRows = projectReport?.reduce((acc, row) => {
        if (!acc[row.parentSite]) acc[row.parentSite] = [];
        row.sub_sites?.forEach(subSite => acc[row.parentSite].push(subSite));
        return acc;
    }, {});

    const getTechClass = (type) => {
        switch (type) {
            case 'cancel':
                return 'cancel-tech';
            default:
                return 'vam';
        }
    };

    // Handle search filter change
    const handleSearchFilter = (filterValues) => {
        setFilterValues(filterValues);
    };

    // EXPORT TO EXCEL (same as before)
    const handleExportExcel = async () => {
        try {
            setIsExportingExcel(true);
            dispatch(openExportLoader({ type: "excel", message: `Exporting Project Management...` }));

            const flattenedData = [];
            Object.entries(groupedRows).forEach(([parentSite, subSites]) => {
                subSites.forEach((subSite, idx) => {
                    const row = {
                        parentSite: idx === 0 ? parentSite : '',
                        subSiteName: subSite.subSiteName,
                    };
                    columns.slice(2).forEach(col => {
                        const tech = subSite[col] || [];
                        const cancel = subSite[`${col}_cancel`] || [];
                        // row[col] = [
                        //     ...(Array.isArray(tech) ? tech : [tech]),
                        //     ...(cancel.length ? cancel.map(c => `❌ ${c}`) : [])
                        // ].join(", ");
                        row[col] = [
                            ...(Array.isArray(tech) ? tech : [tech]),
                            ...(cancel.length ? cancel.map(c => `${c} (Cancelled)`) : [])
                        ].join(", ");
                    });
                    flattenedData.push(row);
                });
            });

            if (!flattenedData.length) {
                InfoToast("No data to export");
                return;
            }

            const tableHeaders = columns.map(col => col.charAt(0).toUpperCase() + col.slice(1));
            const tableData = flattenedData.map(row => columns.map(col => row[col]));

            const sheetData = [["Project Management Report"], [], tableHeaders, ...tableData];
            const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
            worksheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: columns.length - 1 } }];
            worksheet['!cols'] = columns.map(() => ({ wch: 20 }));

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Project Report");
            XLSX.writeFile(workbook, "Project_Report.xlsx");
        } catch (error) {
            console.error("Excel export failed:", error);
        } finally {
            setIsExportingExcel(false);
            dispatch(closeExportLoader());
        }
    };

    // EXPORT TO PDF (unchanged)
    const handleExportPDF = async () => {
        try {
            setIsExportingPDF(true);
            dispatch(openExportLoader({ type: "pdf", message: `Exporting Project Management...` }));

            const table = document.querySelector('.project-report-table-container table');
            if (!table) {
                InfoToast("No data to export!");
                return;
            }

            // const clonedTable = table.cloneNode(true);
            // clonedTable.querySelectorAll("td").forEach(td => {
            //     const text = td.textContent.trim();
            //     td.textContent = text.length > 150 ? text.substring(0, 147) + "..." : text;
            // });
            const clonedTable = table.cloneNode(true);
            clonedTable.querySelectorAll("td").forEach(td => {
                let text = td.textContent.trim();
                text = text.replace(/\s*\(Cancelled\)/gi, " (Cancelled)"); // ensure consistent formatting
                td.textContent = text.length > 150 ? text.substring(0, 147) + "..." : text;
            });
            const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
            const title = "Project Management Report";

            doc.autoTable({
                html: clonedTable,
                startY: 40,
                styles: { fontSize: 8, cellPadding: 4, halign: "center", valign: "middle" },
                headStyles: { fillColor: [60, 60, 60], textColor: [255, 255, 255] },
                alternateRowStyles: { fillColor: [245, 245, 245] },
                theme: "grid",
                didDrawPage: (data) => {
                    const pageWidth = doc.internal.pageSize.getWidth();
                    const pageHeight = doc.internal.pageSize.getHeight();
                    doc.setFontSize(14);
                    doc.setFont("helvetica", "bold");
                    doc.text(title, pageWidth / 2, 25, { align: "center" });
                    const pageCount = doc.internal.getNumberOfPages();
                    const pageText = `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`;
                    doc.setFontSize(9);
                    doc.text(pageText, pageWidth / 2, pageHeight - 10, { align: "center" });
                },
            });

            doc.save("Project_Report.pdf");
        } catch (error) {
            console.error("PDF export failed:", error);
        } finally {
            setIsExportingPDF(false);
            dispatch(closeExportLoader());
        }
    };

    return (
        <div className='project-report-table-container'>
            <ProjectReportSearchFilter
                tableTitle="Project Management"
                searchFilter={handleSearchFilter}
                onExportPDF={handleExportPDF}
                onExportExcel={handleExportExcel}
                isExportingPDF={isExportingPDF}
                isExportingExcel={isExportingExcel}
            />

            <div className="table-container">
                <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                    <Table sx={{ minWidth: 650 }} aria-label="project-report-table">
                        <TableHead className="table-header">
                            <TableRow>
                                <TableCell padding="checkbox"></TableCell>
                                {columns.map(col => (
                                    <TableCell key={col} className="table-cell" align="start">
                                        {col.charAt(0).toUpperCase() + col.slice(1)}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={columns.length + 1} align="center">
                                        <Loader />
                                    </TableCell>
                                </TableRow>
                            ) : Object.keys(groupedRows).length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={columns.length + 1} align="center">
                                        <div className='no-data-message'>No data found</div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                Object.entries(groupedRows).map(([parentSite, subSites]) =>
                                    subSites.map((row, index) => (
                                        <TableRow key={`${row.subSiteName}-${index}`} sx={{ backgroundColor: 'white' }}>
                                            <TableCell padding="checkbox" />
                                            <TableCell
                                                sx={{
                                                    maxWidth: '250px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    fontSize: '1rem',
                                                    fontFamily: 'Inter',
                                                    fontWeight: 400,
                                                    color: '#111827'
                                                }}
                                            >
                                                <EllipsisWithTooltip title={parentSite}>
                                                    <span style={{ visibility: index === 0 ? "visible" : "hidden" }}>
                                                        {parentSite}
                                                    </span>
                                                </EllipsisWithTooltip>
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    maxWidth: '200px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    fontSize: '1rem',
                                                    fontFamily: 'Inter',
                                                    fontWeight: 400,
                                                    color: '#111827'
                                                }}
                                            >
                                                <EllipsisWithTooltip title={row.subSiteName}>
                                                    {row.subSiteName}
                                                </EllipsisWithTooltip>
                                            </TableCell>

                                            {columns.slice(2).map(col => {
                                                const techList = row[col] || [];
                                                const cancelList = row[`${col}_cancel`] || [];
                                                return (
                                                    <TableCell key={col}>
                                                        <EllipsisWithTooltip>
                                                            {techList.length === 0 && cancelList.length === 0 ? (
                                                                '-'
                                                            ) : (
                                                                <>
                                                                    {techList?.map((val, i) => (
                                                                        <div key={`tech-${i}`} className={`technology-container ${getTechClass('tech')}`}>
                                                                            <span className="multi-value">{val}</span>
                                                                        </div>
                                                                    ))}
                                                                    {/* {cancelList.map((val, i) => (
                                                                        <div key={`cancel-${i}`} className={`technology-container ${getTechClass('cancel')}`}>
                                                                            <span className="multi-value"> {val}</span>
                                                                        </div>
                                                                    ))} */}
                                                                    {cancelList?.map((val, i) => (
                                                                        <div key={`cancel-${i}`} className={`technology-container ${getTechClass('cancel')}`}>
                                                                            <span className="multi-value cancel-strike">{val}</span>
                                                                            <span className="cancel-label">(Cancelled)</span>
                                                                        </div>
                                                                    ))}
                                                                </>
                                                            )}
                                                        </EllipsisWithTooltip>
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    ))
                                )
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
        </div>
    );
}

export default ProjectReportTable;
